import React, { useEffect, useMemo, useState } from 'react';
import { ref, onValue, push, update, increment } from 'firebase/database';
import { onValueThrottled } from '../utils/rtdbThrottle';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    FiAlertTriangle,
    FiCheckCircle,
    FiChevronDown,
    FiClock,
    FiDownloadCloud,
    FiEdit3,
    FiPause,
    FiPlay,
    FiPlus,
    FiRefreshCw,
    FiTrash2,
    FiX,
    FiXCircle,
} from 'react-icons/fi';

import { db } from '../firebase';
import CustomDropdown from '../components/CustomDropdown';

// UpdatePage - scheduled / automatic MusicDJ Player update campaigns.
//
// Campaign model: this page writes ONE node under mdm/update_campaigns/<id>. The
// EXECUTOR is a backend Cloud Function (updateCampaignRunner, sweeps every minute,
// standing campaigns every 5 min) which drives the fleet using ONLY commands the
// already-installed monitoring app understands (free_space, install:pkg:version),
// verifies success via each device's reported app_list version, and writes reports
// under mdm/update_reports/<id>/<MAC>. No open browser tab is needed - offline
// devices are caught by a later sweep when they come back (until campaign expiry).
// Works the same for 1 or 5000 devices; wavePerMinute paces mass rollouts.
//
// Times are BELGRADE wall time: the picker value is converted to epoch ms using
// the Europe/Belgrade offset (the runner compares epochs, TZ-independent).

const PLAYER_PKG = 'rs.musicdj.player';
const PLAYER_STORE_KEY = 'rs_musicdj_player';

// ---------------------------------------------------------------------------
// Belgrade time helpers
// ---------------------------------------------------------------------------

/** 'YYYY-MM-DDTHH:mm' (picker) interpreted as Europe/Belgrade wall time -> epoch ms. */
function belgradeToEpoch(local) {
    if (!local) return null;
    const [d, t] = local.split('T');
    const [y, m, day] = d.split('-').map(Number);
    const [hh, mm] = t.split(':').map(Number);
    let ts = Date.UTC(y, m - 1, day, hh, mm);
    // Two passes resolve the zone offset (handles DST boundaries well enough).
    for (let i = 0; i < 2; i++) {
        const tz = new Date(new Date(ts).toLocaleString('en-US', { timeZone: 'Europe/Belgrade' }));
        const utc = new Date(new Date(ts).toLocaleString('en-US', { timeZone: 'UTC' }));
        ts = Date.UTC(y, m - 1, day, hh, mm) - (tz - utc);
    }
    return ts;
}

function formatBelgrade(ms) {
    if (!ms) return '-';
    return new Date(ms).toLocaleString('sv-SE', { timeZone: 'Europe/Belgrade' }).slice(0, 16);
}

/** epoch ms -> 'YYYY-MM-DDTHH:mm' in Europe/Belgrade (datetime-local prefill for Edit). */
function epochToBelgradeLocal(ms) {
    if (!ms) return '';
    return formatBelgrade(ms).replace(' ', 'T');
}

// ---------------------------------------------------------------------------
// Fleet helpers (mirror the device-side engine semantics)
// ---------------------------------------------------------------------------

function playerVersionOf(device) {
    const list = device?.app_list || [];
    const app = list.find((a) => a?.packageName === PLAYER_PKG);
    return app?.versionName || app?.appVersion || null;
}

/** Entry "1.5" also matches "1.5.300" - same prefix semantics as the engine. */
function listMatches(list, version) {
    if (!list || !version) return false;
    return list.some((e) => e === version || version.startsWith(e + '.'));
}

function deviceMatchesCampaign(mac, device, campaign) {
    const v = playerVersionOf(device);
    if (!v) return false;
    const f = campaign.filter || {};
    if (Array.isArray(f.macs) && f.macs.length > 0 && !f.macs.includes(mac)) return false;
    if (Array.isArray(f.groups) && f.groups.length > 0 && !f.groups.includes(device.group)) return false;
    if (listMatches(f.excludeVersions, v)) return false;
    if (f.fromVersions !== 'any' && Array.isArray(f.fromVersions) && !listMatches(f.fromVersions, v)) return false;
    if (campaign.target?.versionName && campaign.target.versionName === v) return false; // already there
    return true;
}

// ---------------------------------------------------------------------------

export default function UpdatePage() {
    const [devices, setDevices] = useState({});
    const [groups, setGroups] = useState({});
    const [playerStore, setPlayerStore] = useState(null);
    const [campaigns, setCampaigns] = useState({});
    const [wizardOpen, setWizardOpen] = useState(false);
    const [editing, setEditing] = useState(null); // { cid, campaign } when editing an existing one

    useEffect(() => {
        const unsubs = [
            onValueThrottled(ref(db, 'mdm/devices'), (s) => setDevices(s.val() || {})),
            onValue(ref(db, 'mdm/groups'), (s) => setGroups(s.val() || {})),
            onValue(ref(db, `mdm/app_store/${PLAYER_STORE_KEY}`), (s) => setPlayerStore(s.val() || null)),
            onValue(ref(db, 'mdm/update_campaigns'), (s) => setCampaigns(s.val() || {})),
        ];
        return () => unsubs.forEach((u) => u());
    }, []);

    // versionName -> { total, online } across the fleet (drives the wizard counters).
    const versionCounts = useMemo(() => {
        const map = {};
        Object.values(devices).forEach((d) => {
            const v = playerVersionOf(d);
            if (!v) return;
            if (!map[v]) map[v] = { total: 0, online: 0 };
            map[v].total += 1;
            if (d.status === 'online') map[v].online += 1;
        });
        return map;
    }, [devices]);

    const campaignList = useMemo(
        () => Object.entries(campaigns).sort(([, a], [, b]) => (b.createdAt || 0) - (a.createdAt || 0)),
        [campaigns]
    );

    return (
        <div className="page-shell">
            <ToastContainer position="top-right" autoClose={3000} theme="dark" />

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-white inline-flex items-center gap-2">
                        <FiDownloadCloud className="text-brand-400" /> Player Updates
                    </h1>
                    <p className="text-sm text-gray-400 mt-1">
                        Scheduled and automatic MusicDJ Player updates - executed by a backend runner
                        (sweeps every minute), verified per device. Times are Belgrade time.
                    </p>
                </div>
                <button
                    onClick={() => { setEditing(null); setWizardOpen((v) => !v); }}
                    className="btn-primary self-start sm:self-auto"
                >
                    {wizardOpen ? <><FiX /> Close</> : <><FiPlus /> New Campaign</>}
                </button>
            </div>

            {wizardOpen && (
                <CampaignWizard
                    key={editing?.cid || 'new'}
                    devices={devices}
                    groups={groups}
                    playerStore={playerStore}
                    versionCounts={versionCounts}
                    initial={editing?.campaign || null}
                    editCid={editing?.cid || null}
                    onDone={() => { setWizardOpen(false); setEditing(null); }}
                />
            )}

            <div className="space-y-4 mt-2">
                {campaignList.length === 0 && !wizardOpen && (
                    <div className="card-chrome p-8 text-center text-gray-500">
                        No update campaigns yet. Create one to roll out a Player version across the fleet.
                    </div>
                )}
                {campaignList.map(([cid, c]) => (
                    <CampaignCard
                        key={cid}
                        cid={cid}
                        campaign={c}
                        devices={devices}
                        onEdit={() => {
                            setEditing({ cid, campaign: c });
                            setWizardOpen(true);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                    />
                ))}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Wizard
// ---------------------------------------------------------------------------

function CampaignWizard({ devices, groups, playerStore, versionCounts, initial, editCid, onDone }) {
    const storeVersions = useMemo(() => {
        const out = [];
        Object.entries(playerStore?.versions || {}).forEach(([key, v]) => {
            if (v?.versionName) out.push({ key, versionName: v.versionName });
        });
        // Highest versionCode (numeric key) first.
        return out.sort((a, b) => Number(b.key) - Number(a.key));
    }, [playerStore]);
    const latestName = playerStore?.latestVersion || null;

    // Prefill from `initial` when editing (component remounts per campaign via key=).
    const iF = initial?.filter || {};
    const iS = initial?.schedule || {};
    const iO = initial?.options || {};
    const iMacs = Array.isArray(iF.macs) ? iF.macs : [];
    const iGroups = Array.isArray(iF.groups) ? iF.groups : [];

    const [name, setName] = useState(initial?.name || '');
    const [targetMode, setTargetMode] = useState(initial?.target?.mode || 'latest');
    const [targetKey, setTargetKey] = useState(initial?.target?.versionKey || '');
    const [fromAny, setFromAny] = useState(iF.fromVersions == null || iF.fromVersions === 'any');
    const [fromVersions, setFromVersions] = useState(Array.isArray(iF.fromVersions) ? iF.fromVersions : []);
    const [excludeVersions, setExcludeVersions] = useState(Array.isArray(iF.excludeVersions) ? iF.excludeVersions : []);
    const [scope, setScope] = useState(iMacs.length > 0 ? 'macs' : iGroups.length > 0 ? 'groups' : 'all');
    const [scopeGroups, setScopeGroups] = useState(iGroups);
    const [scopeMacsText, setScopeMacsText] = useState(iMacs.join('\n'));
    const [schedMode, setSchedMode] = useState(iS.mode || 'now');   // now | at | on_online
    const [startLocal, setStartLocal] = useState(iS.startAtMs ? epochToBelgradeLocal(iS.startAtMs) : '');
    const [expiresDays, setExpiresDays] = useState(
        iS.expiresAtMs && iS.startAtMs
            ? String(Math.max(1, Math.round((iS.expiresAtMs - iS.startAtMs) / 86400000)))
            : '7'
    ); // '' = never
    const [storagePrecheck, setStoragePrecheck] = useState(iO.storagePrecheck !== false);
    const [minFreeMb, setMinFreeMb] = useState(iO.minFreeMb || 500);
    const [wavePerMinute, setWavePerMinute] = useState(iO.wavePerMinute || 30);
    const [maxRetries, setMaxRetries] = useState(iO.maxRetries != null ? iO.maxRetries : 2);
    const [creating, setCreating] = useState(false);

    const targetVersionName = targetMode === 'latest'
        ? latestName
        : storeVersions.find((v) => v.key === targetKey)?.versionName || null;

    // Manual MAC list: one per line or comma-separated; normalised to UPPERCASE (RTDB keys).
    const scopeMacs = useMemo(
        () => scopeMacsText.split(/[\s,;]+/).map((m) => m.trim().toUpperCase()).filter((m) => m.length >= 11),
        [scopeMacsText]
    );

    // Live estimate of matched devices with the current wizard settings.
    const preview = useMemo(() => {
        const fakeCampaign = {
            target: { versionName: targetVersionName },
            filter: {
                fromVersions: fromAny ? 'any' : fromVersions,
                excludeVersions,
                groups: scope === 'groups' ? scopeGroups : null,
                macs: scope === 'macs' ? scopeMacs : null,
            },
        };
        let matched = 0, online = 0;
        Object.entries(devices).forEach(([mac, d]) => {
            if (!deviceMatchesCampaign(mac, d, fakeCampaign)) return;
            if (scope === 'online_now' && d.status !== 'online') return;
            matched += 1;
            if (d.status === 'online') online += 1;
        });
        return { matched, online };
    }, [devices, targetVersionName, fromAny, fromVersions, excludeVersions, scope, scopeGroups, scopeMacs]);

    const toggle = (setter) => (value) =>
        setter((prev) => (prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]));

    const create = async () => {
        if (!targetVersionName) {
            toast.error('Target version unavailable - upload the Player to the App Store and set latestVersion first');
            return;
        }
        if (targetMode === 'specific' && !targetKey) {
            toast.error('Pick a specific target version');
            return;
        }
        if (!fromAny && fromVersions.length === 0) {
            toast.error('Pick at least one source version (or use "any")');
            return;
        }
        if (schedMode === 'at' && !startLocal) {
            toast.error('Pick the scheduled start time');
            return;
        }
        if (scope === 'groups' && scopeGroups.length === 0) {
            toast.error('Pick at least one group');
            return;
        }
        if (scope === 'macs' && scopeMacs.length === 0) {
            toast.error('Enter at least one MAC address');
            return;
        }

        const now = Date.now();
        const startAtMs = schedMode === 'at' ? belgradeToEpoch(startLocal) : now;
        // "online now" scope = snapshot the currently matching online devices into an
        // explicit MAC list (the engine itself has no notion of dashboard-side "online").
        let macs = null;
        if (scope === 'macs') {
            macs = scopeMacs;
        }
        if (scope === 'online_now') {
            macs = Object.entries(devices)
                .filter(([mac, d]) => d.status === 'online' && deviceMatchesCampaign(mac, d, {
                    target: { versionName: targetVersionName },
                    filter: { fromVersions: fromAny ? 'any' : fromVersions, excludeVersions, groups: null, macs: null },
                }))
                .map(([mac]) => mac);
            if (macs.length === 0) {
                toast.error('No online devices match the current filters');
                return;
            }
        }

        const resolvedKey = targetMode === 'latest'
            ? (storeVersions.find((v) => v.versionName === latestName)?.key || null)
            : targetKey;

        const campaign = {
            name: name.trim() || `Player -> ${targetVersionName} (${formatBelgrade(now)})`,
            createdAt: now,
            createdBy: 'mdm-web',
            status: 'active',
            app: PLAYER_PKG,
            target: { mode: targetMode, versionKey: resolvedKey, versionName: targetVersionName },
            filter: {
                fromVersions: fromAny ? 'any' : fromVersions,
                excludeVersions: excludeVersions.length > 0 ? excludeVersions : null,
                groups: scope === 'groups' ? scopeGroups : null,
                macs,
            },
            schedule: {
                mode: schedMode,
                startAtMs,
                expiresAtMs: expiresDays ? startAtMs + Number(expiresDays) * 86400000 : null,
            },
            options: {
                storagePrecheck,
                minFreeMb: Number(minFreeMb) || 500,
                wavePerMinute: Number(wavePerMinute) || 30,
                maxRetries: Number(maxRetries) || 2,
                allowDowngrade: false,
            },
            stats: { success: 0, failed: 0 },
        };

        try {
            setCreating(true);
            if (editCid) {
                // Edit: keep createdAt/createdBy/stats and existing reports; the runner simply
                // follows the new values on its next sweep. Editing re-activates the campaign
                // (that is why you edit a paused/cancelled/completed one).
                await update(ref(db, `mdm/update_campaigns/${editCid}`), {
                    name: campaign.name,
                    app: campaign.app,
                    target: campaign.target,
                    filter: campaign.filter,
                    schedule: campaign.schedule,
                    options: campaign.options,
                    status: 'active',
                });
                toast.success(`Campaign updated and active - ${preview.matched} device(s) match right now`);
            } else {
                await push(ref(db, 'mdm/update_campaigns'), campaign);
                toast.success(`Campaign created - ${preview.matched} device(s) match right now`);
            }
            onDone?.();
        } catch (e) {
            toast.error(`Failed to save campaign: ${e.message}`);
        } finally {
            setCreating(false);
        }
    };

    const fieldLabel = 'block text-xs uppercase tracking-wider text-gray-400 mb-1.5';
    const chip = (active, danger = false) =>
        `px-2.5 py-1 rounded-lg text-xs font-medium border cursor-pointer transition-colors ${
            active
                ? danger
                    ? 'bg-red-500/15 border-red-500/40 text-red-300'
                    : 'brand-pill'
                : 'bg-white/[0.03] border-white/[0.08] text-gray-300 hover:border-white/[0.2]'
        }`;

    return (
        <section className="card-chrome p-5 mb-6">
            <h2 className="text-base font-semibold text-brand-400 mb-4">
                {editCid ? `Edit Campaign: ${initial?.name || editCid}` : 'New Update Campaign'}
            </h2>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left column - what and who */}
                <div className="space-y-4">
                    <div>
                        <label className={fieldLabel}>Campaign name (optional)</label>
                        <input value={name} onChange={(e) => setName(e.target.value)}
                               placeholder={`Player -> ${targetVersionName || '?'}`} className="input-field" />
                    </div>

                    <div>
                        <label className={fieldLabel}>Target version</label>
                        <div className="flex flex-wrap items-center gap-2">
                            <span className={chip(targetMode === 'latest')} onClick={() => setTargetMode('latest')}>
                                Latest from App Store {latestName ? `(${latestName})` : '(not set!)'}
                            </span>
                            <span className={chip(targetMode === 'specific')} onClick={() => setTargetMode('specific')}>
                                Specific version
                            </span>
                        </div>
                        {targetMode === 'specific' && (
                            <div className="mt-2 max-w-[240px]">
                                <CustomDropdown
                                    value={targetKey}
                                    onChange={setTargetKey}
                                    options={storeVersions.map((v) => ({ label: `${v.versionName} (code ${v.key})`, value: v.key }))}
                                    placeholder="Pick version..."
                                />
                            </div>
                        )}
                    </div>

                    <div>
                        <label className={fieldLabel}>Update devices currently on (source versions)</label>
                        <div className="flex flex-wrap gap-2">
                            <span className={chip(fromAny)} onClick={() => setFromAny(true)}>Any version</span>
                            {Object.entries(versionCounts)
                                .sort(([a], [b]) => b.localeCompare(a, undefined, { numeric: true }))
                                .map(([v, c]) => (
                                    <span key={v}
                                          className={chip(!fromAny && fromVersions.includes(v))}
                                          onClick={() => { setFromAny(false); toggle(setFromVersions)(v); }}>
                                        {v} - {c.total} dev ({c.online} online)
                                    </span>
                                ))}
                        </div>
                    </div>

                    <div>
                        <label className={fieldLabel}>Exclude versions (never touched)</label>
                        <div className="flex flex-wrap gap-2">
                            {Object.keys(versionCounts)
                                .sort((a, b) => b.localeCompare(a, undefined, { numeric: true }))
                                .map((v) => (
                                    <span key={v} className={chip(excludeVersions.includes(v), true)}
                                          onClick={() => toggle(setExcludeVersions)(v)}>
                                        {v}
                                    </span>
                                ))}
                        </div>
                    </div>

                    <div>
                        <label className={fieldLabel}>Scope</label>
                        <div className="flex flex-wrap gap-2">
                            <span className={chip(scope === 'all')} onClick={() => setScope('all')}>Whole fleet</span>
                            <span className={chip(scope === 'groups')} onClick={() => setScope('groups')}>Groups</span>
                            <span className={chip(scope === 'online_now')} onClick={() => setScope('online_now')}>
                                Only devices online right now (snapshot)
                            </span>
                            <span className={chip(scope === 'macs')} onClick={() => setScope('macs')}>
                                Manual MAC list (test / surgical)
                            </span>
                        </div>
                        {scope === 'macs' && (
                            <textarea
                                value={scopeMacsText}
                                onChange={(e) => setScopeMacsText(e.target.value)}
                                placeholder={'One MAC per line, e.g.\nD0:76:58:5A:46:8C'}
                                rows={3}
                                className="input-field mt-2 font-mono text-xs"
                            />
                        )}
                        {scope === 'groups' && (
                            <div className="flex flex-wrap gap-2 mt-2">
                                {Object.entries(groups).map(([gid, g]) => (
                                    <span key={gid} className={chip(scopeGroups.includes(gid))}
                                          onClick={() => toggle(setScopeGroups)(gid)}>
                                        {g?.name || gid}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right column - when and how */}
                <div className="space-y-4">
                    <div>
                        <label className={fieldLabel}>Schedule (Belgrade time)</label>
                        <div className="flex flex-wrap gap-2">
                            <span className={chip(schedMode === 'now')} onClick={() => setSchedMode('now')}>Start now</span>
                            <span className={chip(schedMode === 'at')} onClick={() => setSchedMode('at')}>Scheduled</span>
                            <span className={chip(schedMode === 'on_online')} onClick={() => setSchedMode('on_online')}>
                                When device comes online (standing)
                            </span>
                        </div>
                        {schedMode === 'at' && (
                            <input type="datetime-local" value={startLocal}
                                   onChange={(e) => setStartLocal(e.target.value)}
                                   className="input-field mt-2 max-w-[240px]" />
                        )}
                        <div className="flex items-center gap-2 mt-3">
                            <span className="text-sm text-gray-400">Campaign expires after</span>
                            <input type="number" min="0" value={expiresDays}
                                   onChange={(e) => setExpiresDays(e.target.value)}
                                   className="input-field w-20" />
                            <span className="text-sm text-gray-400">days (empty = never; offline devices catch up until expiry)</span>
                        </div>
                    </div>

                    <div>
                        <label className={fieldLabel}>Storage pre-check</label>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                            <input type="checkbox" checked={storagePrecheck}
                                   onChange={(e) => setStoragePrecheck(e.target.checked)} />
                            Free storage first (trim caches + clear Downloads) when below
                            <input type="number" min="100" value={minFreeMb}
                                   onChange={(e) => setMinFreeMb(e.target.value)}
                                   className="input-field w-24" disabled={!storagePrecheck} />
                            MB free
                        </label>
                    </div>

                    <div className="grid grid-cols-2 gap-3 max-w-[360px]">
                        <div>
                            <label className={fieldLabel}>Wave (devices/min)</label>
                            <input type="number" min="1" value={wavePerMinute}
                                   onChange={(e) => setWavePerMinute(e.target.value)} className="input-field" />
                            <p className="text-[11px] text-gray-500 mt-1">
                                Max install commands sent per minute - paces mass rollouts (5000 devices at 30/min = ~3h).
                            </p>
                        </div>
                        <div>
                            <label className={fieldLabel}>Max retries</label>
                            <input type="number" min="0" max="5" value={maxRetries}
                                   onChange={(e) => setMaxRetries(e.target.value)} className="input-field" />
                        </div>
                    </div>

                    <div className="card-inset p-4">
                        <div className="text-sm text-gray-300">
                            Matches <span className="text-brand-400 font-semibold">{preview.matched}</span> device(s) right now
                            {' '}({preview.online} online{scope !== 'online_now' ? ', offline devices catch up automatically' : ''}).
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            Target: <span className="text-gray-300">{targetVersionName || 'unknown'}</span>. Downgrade is always blocked.
                            Runs in the backend (Cloud Function) - no need to keep this tab open. Success is
                            verified: a device counts only when it reports the target version back.
                        </div>
                    </div>

                    <button onClick={create} disabled={creating} className="btn-primary">
                        <FiDownloadCloud /> {creating ? 'Saving...' : (editCid ? 'Save changes (re-activates)' : 'Create campaign')}
                    </button>
                </div>
            </div>
        </section>
    );
}

// ---------------------------------------------------------------------------
// Campaign card + detail
// ---------------------------------------------------------------------------

function CampaignCard({ cid, campaign, devices, onEdit }) {
    const [expanded, setExpanded] = useState(false);
    const [reports, setReports] = useState(null);
    const [stateFilter, setStateFilter] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);

    // Delete removes the campaign AND its per-device reports. A live campaign simply
    // stops being processed (the runner ignores missing campaigns); commands already
    // sent to devices still finish on the device, they just stop being tracked.
    const deleteCampaign = async () => {
        setConfirmDelete(false);
        try {
            await update(ref(db), {
                [`mdm/update_campaigns/${cid}`]: null,
                [`mdm/update_reports/${cid}`]: null,
            });
            toast.success('Campaign and its reports deleted');
        } catch (e) {
            toast.error(`Delete failed: ${e.message}`);
        }
    };

    useEffect(() => {
        if (!expanded) return undefined;
        const unsub = onValue(ref(db, `mdm/update_reports/${cid}`), (s) => setReports(s.val() || {}));
        return () => unsub();
    }, [expanded, cid]);

    const matched = useMemo(
        () => Object.entries(devices).filter(([mac, d]) => deviceMatchesCampaign(mac, d, campaign)).length,
        [devices, campaign]
    );
    const success = campaign.stats?.success || 0;
    const failed = campaign.stats?.failed || 0;
    const pendingEst = Math.max(0, matched);
    const total = success + failed + pendingEst;

    const status = campaign.status || 'active';
    const statusTone = {
        active: 'bg-green-500/15 text-green-400 border-green-500/30',
        paused: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
        cancelled: 'bg-red-500/15 text-red-400 border-red-500/30',
        completed: 'bg-white/[0.06] text-gray-300 border-white/[0.1]',
    }[status] || 'bg-white/[0.06] text-gray-300 border-white/[0.1]';

    const setStatus = async (next) => {
        try {
            await update(ref(db, `mdm/update_campaigns/${cid}`), { status: next });
            toast.success(`Campaign ${next}`);
        } catch (e) {
            toast.error(`Failed: ${e.message}`);
        }
    };

    // Re-arm every finally-failed device: clear final/retries/counted and roll the
    // stats.failed counter back for the ones that were already counted.
    const retryFailed = async () => {
        try {
            const snap = reports || {};
            const failedMacs = Object.entries(snap).filter(([, r]) => r?.state === 'failed');
            if (failedMacs.length === 0) {
                toast.info('No failed devices to retry');
                return;
            }
            const updates = {};
            let counted = 0;
            failedMacs.forEach(([mac, r]) => {
                updates[`mdm/update_reports/${cid}/${mac}/final`] = null;
                updates[`mdm/update_reports/${cid}/${mac}/retries`] = 0;
                updates[`mdm/update_reports/${cid}/${mac}/counted`] = null;
                updates[`mdm/update_reports/${cid}/${mac}/state`] = 'skipped';
                updates[`mdm/update_reports/${cid}/${mac}/reason`] = 'retry_requested';
                if (r?.counted) counted += 1;
            });
            if (counted > 0) {
                updates[`mdm/update_campaigns/${cid}/stats/failed`] = increment(-counted);
            }
            await update(ref(db), updates);
            toast.success(`Re-armed ${failedMacs.length} failed device(s) - they retry on their next check-in`);
        } catch (e) {
            toast.error(`Retry failed: ${e.message}`);
        }
    };

    const exportCsv = () => {
        const rows = [['mac', 'device', 'state', 'reason_or_error', 'from', 'to', 'freedMb', 'retries', 'finishedAt_belgrade']];
        Object.entries(reports || {}).forEach(([mac, r]) => {
            rows.push([
                mac, devices[mac]?.name || '', r.state || '', r.error || r.reason || '',
                r.fromVersion || '', r.toVersion || '', r.freedMb ?? '', r.retries ?? 0,
                formatBelgrade(r.finishedAt),
            ]);
        });
        const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        a.download = `update-report-${cid}.csv`;
        a.click();
    };

    const scheduleDesc = (() => {
        const s = campaign.schedule || {};
        if (s.mode === 'at') return `Scheduled ${formatBelgrade(s.startAtMs)} (Belgrade)`;
        if (s.mode === 'on_online') return 'When device comes online (standing)';
        return `Started ${formatBelgrade(campaign.createdAt)}`;
    })();

    const reportRows = useMemo(() => {
        const rows = Object.entries(reports || {}).map(([mac, r]) => ({ mac, ...r }));
        rows.sort((a, b) => (b.finishedAt || b.startedAt || 0) - (a.finishedAt || a.startedAt || 0));
        return stateFilter ? rows.filter((r) => r.state === stateFilter) : rows;
    }, [reports, stateFilter]);

    return (
        <section className="card-chrome p-5">
            <div className="flex flex-col lg:flex-row lg:items-center gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-white font-semibold truncate">{campaign.name || cid}</h3>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${statusTone}`}>{status}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                        Target <span className="text-gray-300">{campaign.target?.versionName}</span>
                        {' - '}{scheduleDesc}
                        {campaign.schedule?.expiresAtMs ? ` - expires ${formatBelgrade(campaign.schedule.expiresAtMs)}` : ''}
                    </div>
                </div>

                <div className="flex items-center gap-4 text-sm shrink-0">
                    <span className="inline-flex items-center gap-1 text-green-400"><FiCheckCircle /> {success}</span>
                    <span className="inline-flex items-center gap-1 text-red-400"><FiXCircle /> {failed}</span>
                    <span className="inline-flex items-center gap-1 text-gray-400"><FiClock /> {pendingEst} matching now</span>
                </div>

                <div className="flex items-center gap-2 shrink-0 flex-wrap">
                    {status === 'active' && (
                        <button onClick={() => setStatus('paused')} className="btn-secondary" title="Pause - devices stop starting new updates">
                            <FiPause /> Pause
                        </button>
                    )}
                    {status === 'paused' && (
                        <button onClick={() => setStatus('active')} className="btn-secondary"><FiPlay /> Resume</button>
                    )}
                    <button onClick={onEdit} className="btn-secondary" title="Edit campaign (saving re-activates it)">
                        <FiEdit3 /> Edit
                    </button>
                    {status !== 'cancelled' && (
                        <button onClick={() => setStatus('cancelled')} className="btn-secondary text-red-300"><FiX /> Cancel</button>
                    )}
                    <button onClick={() => setConfirmDelete(true)} className="btn-secondary text-red-300" title="Delete campaign + its reports">
                        <FiTrash2 /> Delete
                    </button>
                    <button onClick={() => setExpanded((v) => !v)} className="btn-secondary">
                        <FiChevronDown className={`transition-transform ${expanded ? 'rotate-180' : ''}`} /> Details
                    </button>
                </div>
            </div>

            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                     onClick={() => setConfirmDelete(false)}>
                    <div className="card-chrome w-full max-w-md p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
                        <h3 className="text-base font-semibold text-gray-100 inline-flex items-center gap-2 mb-3">
                            <FiAlertTriangle className="text-red-400" /> Delete campaign?
                        </h3>
                        <p className="text-sm text-gray-400 leading-relaxed">
                            This permanently deletes "{campaign.name || cid}" and ALL its per-device reports
                            ({success} success / {failed} failed). Installs already sent to devices will still
                            finish, but will no longer be tracked. Continue?
                        </p>
                        <div className="flex justify-end gap-2 pt-5">
                            <button onClick={() => setConfirmDelete(false)} className="btn-secondary">Cancel</button>
                            <button onClick={deleteCampaign}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium bg-red-500/15 hover:bg-red-500/25 text-red-400 transition">
                                <FiTrash2 /> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {total > 0 && (
                <div className="mt-3 h-1.5 rounded-full bg-white/[0.06] overflow-hidden flex">
                    <div className="bg-green-500" style={{ width: `${(success / total) * 100}%` }} />
                    <div className="bg-red-500" style={{ width: `${(failed / total) * 100}%` }} />
                </div>
            )}

            {expanded && (
                <div className="mt-4 border-t border-white/[0.06] pt-4">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                        {['', 'success', 'failed', 'install_sent', 'freeing', 'skipped'].map((f) => (
                            <button key={f || 'all'} onClick={() => setStateFilter(f)}
                                    className={`px-2.5 py-1 rounded-lg text-xs border transition-colors ${
                                        stateFilter === f ? 'brand-pill' : 'bg-white/[0.03] border-white/[0.08] text-gray-300'
                                    }`}>
                                {f || 'all'}
                            </button>
                        ))}
                        <div className="flex-1" />
                        <button onClick={retryFailed} className="btn-secondary"><FiRefreshCw /> Retry failed</button>
                        <button onClick={exportCsv} className="btn-secondary">Export CSV</button>
                    </div>

                    {reports === null ? (
                        <p className="text-sm text-gray-500">Loading reports...</p>
                    ) : reportRows.length === 0 ? (
                        <p className="text-sm text-gray-500 inline-flex items-center gap-2">
                            <FiAlertTriangle /> No device reports yet - the backend runner writes reports as it processes devices (sweeps every minute).
                        </p>
                    ) : (
                        <div className="overflow-x-auto scroll-brand">
                            <table className="w-full min-w-[860px] text-sm">
                                <thead>
                                    <tr className="text-xs uppercase tracking-wider text-gray-500 text-left">
                                        <th className="py-2 pr-4">Device</th>
                                        <th className="py-2 pr-4">MAC</th>
                                        <th className="py-2 pr-4">From &gt; To</th>
                                        <th className="py-2 pr-4">State</th>
                                        <th className="py-2 pr-4">Reason / Error</th>
                                        <th className="py-2 pr-4">Freed MB</th>
                                        <th className="py-2 pr-4">Retries</th>
                                        <th className="py-2">Finished (BG)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {reportRows.map((r) => (
                                        <tr key={r.mac} className="border-t border-white/[0.04]">
                                            <td className="py-2 pr-4 text-gray-300">{devices[r.mac]?.name || '-'}</td>
                                            <td className="py-2 pr-4 font-mono text-xs text-gray-400">{r.mac}</td>
                                            <td className="py-2 pr-4 text-gray-300">
                                                {r.fromVersion || '?'} &gt; {r.toVersion || campaign.target?.versionName || '?'}
                                            </td>
                                            <td className="py-2 pr-4">
                                                <StateBadge state={r.state} final={r.final} />
                                            </td>
                                            <td className="py-2 pr-4 text-gray-400 text-xs">{r.error || r.reason || '-'}</td>
                                            <td className="py-2 pr-4 text-gray-400">{r.freedMb ?? '-'}</td>
                                            <td className="py-2 pr-4 text-gray-400">{r.retries ?? 0}</td>
                                            <td className="py-2 text-gray-400 text-xs">{formatBelgrade(r.finishedAt)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

function StateBadge({ state, final: isFinal }) {
    const map = {
        success: 'bg-green-500/15 text-green-400',
        failed: 'bg-red-500/15 text-red-400',
        install_sent: 'bg-blue-500/15 text-blue-300',
        freeing: 'bg-yellow-500/15 text-yellow-300',
        skipped: 'bg-white/[0.06] text-gray-400',
    };
    return (
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${map[state] || 'bg-white/[0.06] text-gray-400'}`}>
            {state || '-'}{state === 'failed' && isFinal ? ' (final)' : ''}
        </span>
    );
}
